import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import { useLimuloIsTheSource } from '@/hooks/useLimuloIsTheSource';
import {
  useCardsQuery,
  useCoachDeckMutation,
  useCoachDeckListMutation,
  useCompleteDeckMutation,
  useCounterDeckMutation,
  useDeckCheckQuery,
  useDeleteSavedDeckMutation,
  useProfileMeQuery,
  useSavedDecksQuery,
  useSaveDeckMutation,
} from '@/services/qbotApi';
import { env } from '@/env';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { buildCardIndex, resolveDeckNames } from '@/lib/crCardIndex';
import { saveDeckErrorKey } from './saveDeckError';
import { isQbotNotLinkedCode } from '@/lib/limulo/crLink';
import { NotLinkedNotice } from './NotLinkedNotice';
import {
  CoachPill,
  CounterPill,
  DeletePill,
  DoctorPill,
  ImportPill,
  SavePill,
} from './DeckActions';
import { CoachOverlay } from './CoachOverlay';
import { AdvisorOverlay } from './AdvisorOverlay';
import { CounterResult, DoctorResult } from './AdvisorResults';
import { ActionRow, DeckStage, StageHeader } from './DeckStage';
import { DeckStats } from './DeckStats';
import type { CRPlayerCard } from '@/types/qbot';

interface MyDecksModeProps {
  isPro: boolean;
  /** Tapping a card on the current deck jumps to Top Decks filtered by it. */
  onPickCard: (card: CRPlayerCard) => void;
  /** Opens the AI builder sheet. */
  onBuild: () => void;
}

/**
 * "My Decks" — your decks, one per page, filling the panel without scrolling.
 *
 * Page 0 is the deck you're playing right now (with its rating); the rest are
 * the decks you saved. Paging swaps the deck + stats inside the fixed
 * `<DeckStage>` frame; the coach answer opens as an overlay so the frame never
 * moves. One deck is on screen at a time, so the coach / save / delete state
 * lives here at the mode level and resets on every page change.
 */
export function MyDecksMode({ isPro, onPickCard, onBuild }: MyDecksModeProps) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const limuloIsTheSource = useLimuloIsTheSource();

  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useProfileMeQuery();
  const tag = profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';
  const { data: check } = useDeckCheckQuery({ tag }, { skip: !tag });
  const { data: savedData } = useSavedDecksQuery();
  const { data: catalog } = useCardsQuery();
  const index = useMemo(() => buildCardIndex(catalog), [catalog]);

  const currentDeck: CRPlayerCard[] = check?.deck ?? profile?.player?.currentDeck ?? [];
  const savedDecks = savedData?.decks ?? [];
  const pageCount = 1 + savedDecks.length;

  const [rawPage, setRawPage] = useState(0);
  const page = Math.min(rawPage, pageCount - 1);

  // One deck on screen → coach / save / delete state is per-mode, reset on turn.
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachByTag, tagCoach] = useCoachDeckMutation();
  const [coachByList, listCoach] = useCoachDeckListMutation();
  const [saveDeck, { isLoading: saving }] = useSaveDeckMutation();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [del, { isLoading: deleting }] = useDeleteSavedDeckMutation();
  const [confirming, setConfirming] = useState(false);

  // Deterministic advisor overlays over the stage: Doctor optimizes THIS deck,
  // Counter builds a counter to it. One at a time; reset on page turn.
  const [advisor, setAdvisor] = useState<'doctor' | 'counter' | null>(null);
  const [runDoctor, doctorState] = useCompleteDeckMutation();
  const [runCounter, counterState] = useCounterDeckMutation();
  const [doctorSaved, setDoctorSaved] = useState(false);
  const [doctorSaveError, setDoctorSaveError] = useState<string | null>(null);

  const goTo = (p: number) => {
    setCoachOpen(false);
    tagCoach.reset();
    listCoach.reset();
    setSaved(false);
    setSaveError(null);
    setConfirming(false);
    setAdvisor(null);
    doctorState.reset();
    counterState.reset();
    setDoctorSaved(false);
    setDoctorSaveError(null);
    setRawPage(p);
  };

  if (profileLoading && currentDeck.length === 0 && savedDecks.length === 0) {
    return <DeckSkeleton />;
  }

  // 🔴 Nel ramo Limulo QBot puo' rispondere «non collegato» a chi ha il profilo CR su Limulo
  // (il Sit&Go scrive li'): «non sono riuscito a caricare» + «riprova» sarebbe una bugia, e
  // riprovare darebbe la stessa risposta. Questa parte parte da `/profile/me` di QBot e NON si
  // ripara qui: si dice che arriva. Sparisce da sola quando QBot legge il profilo Limulo.
  // Fuori dal ramo, e per ogni altro errore, resta il messaggio di sempre.
  if (profileError && !profile && limuloIsTheSource && isQbotNotLinkedCode(profileError)) {
    return <NotLinkedNotice />;
  }

  // A failed lookup is not an empty deck — say so, and offer a retry.
  if (profileError && !profile) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-lg bg-rose-500/10 p-3">
        <p className="text-[11px] leading-snug text-rose-300">{t('panel.mine.profileFailed')}</p>
        <Button variant="link" corpo="maiuscoletto" onClick={() => refetchProfile()}>
          {t('panel.retry')}
        </Button>
      </div>
    );
  }

  const savedDeck = page > 0 ? savedDecks[page - 1] : undefined;

  // Doctor optimizes the deck in view (current → no seed; saved → seed with it);
  // Counter treats the deck in view as the opponent and builds a counter.
  const openDoctor = () => {
    setAdvisor('doctor');
    setDoctorSaved(false);
    setDoctorSaveError(null);
    doctorState.reset();
    runDoctor(savedDeck ? { seedCards: savedDeck.cards.slice(0, 7) } : {}).catch(() => undefined);
  };
  const openCounter = () => {
    setAdvisor('counter');
    counterState.reset();
    const opponentDeck = savedDeck ? savedDeck.cards : currentDeck.map((c) => c.name);
    runCounter({ opponentDeck }).catch(() => undefined);
  };
  const onDoctorSave = async () => {
    const optimized = doctorState.data?.deck;
    if (!optimized) return;
    setDoctorSaveError(null);
    try {
      await saveDeck({
        name: t('panel.doctor.saveName', { archetype: optimized.archetype }),
        cards: optimized.cards.map((c) => c.name),
      }).unwrap();
      haptics.notification('success');
      setDoctorSaved(true);
    } catch (err) {
      haptics.notification('error');
      setDoctorSaveError(t(saveDeckErrorKey(err)));
    }
  };

  // ── Slot contents for the page in view ──
  let name: string;
  let grid: React.ReactNode;
  let stats: React.ReactNode;
  let actions: React.ReactNode;

  if (page === 0) {
    const complete = currentDeck.length === 8;
    name = t('panel.myDecks.current');
    grid = <DeckGrid cards={currentDeck} onCardClick={onPickCard} />;
    stats = complete ? (
      <DeckStats
        archetype={check?.archetype}
        rating={check?.rating}
        avgElixir={check?.avgElixir}
        loading={!check}
      />
    ) : (
      <IncompleteDeck count={currentDeck.length} />
    );
    actions = complete ? (
      <ActionRow error={saveError}>
        <ImportPill cards={currentDeck} label={t('panel.action.import')} />
        <SavePill
          saved={saved}
          saving={saving}
          onSave={async () => {
            setSaveError(null);
            try {
              await saveDeck({
                name: check?.archetype
                  ? t('panel.mine.saveName', { archetype: check.archetype })
                  : t('panel.mine.saveNameFallback'),
                cards: currentDeck.map((c) => c.name),
              }).unwrap();
              haptics.notification('success');
              setSaved(true);
            } catch (err) {
              haptics.notification('error');
              setSaveError(t(saveDeckErrorKey(err)));
            }
          }}
          label={t('panel.action.save')}
          savedLabel={t('panel.action.saved')}
        />
        <CoachPill
          isPro={isPro}
          label={t('panel.action.coach')}
          onClick={() => {
            setCoachOpen(true);
            if (isPro && tag && !tagCoach.data && !tagCoach.isLoading) {
              coachByTag({ tag }).catch(() => undefined);
            }
          }}
        />
        <DoctorPill label={t('panel.action.doctor')} onClick={openDoctor} />
        <CounterPill label={t('panel.action.counter')} onClick={openCounter} />
      </ActionRow>
    ) : null;
  } else if (savedDeck) {
    const { cards, avgElixir } = resolveDeckNames(savedDeck.cards, index);
    name = savedDeck.name;
    grid = <DeckGrid cards={cards} showLevels={false} avgElixir={avgElixir} />;
    stats = (
      <DeckStats archetype={savedDeck.archetype} rating={savedDeck.rating} avgElixir={avgElixir} />
    );
    actions = (
      <ActionRow>
        <ImportPill cards={cards} label={t('panel.action.import')} />
        <DoctorPill label={t('panel.action.doctor')} onClick={openDoctor} />
        <CounterPill label={t('panel.action.counter')} onClick={openCounter} />
        <CoachPill
          isPro={isPro}
          label={t('panel.action.coach')}
          onClick={() => {
            setCoachOpen(true);
            if (isPro && !listCoach.data && !listCoach.isLoading) {
              coachByList({ cards: savedDeck.cards }).catch(() => undefined);
            }
          }}
        />
        <DeletePill
          confirming={confirming}
          deleting={deleting}
          onRequest={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            try {
              await del({ id: savedDeck.id }).unwrap();
              haptics.notification('success');
              goTo(Math.max(0, page - 1));
            } catch {
              haptics.notification('error');
              setConfirming(false);
            }
          }}
          labels={{
            delete: t('panel.action.delete'),
            cancel: t('saved.row.cancel'),
            confirm: t('saved.row.confirm'),
            deleting: t('saved.row.deleting'),
          }}
        />
      </ActionRow>
    );
  } else {
    name = t('panel.tab.myDecks');
    grid = <DeckGrid cards={[]} />;
    stats = null;
    actions = null;
  }

  // The coach mutation that belongs to the page in view.
  const coach = page === 0 ? tagCoach : listCoach;

  return (
    <DeckStage
      page={page}
      total={pageCount}
      onPrev={() => goTo(Math.max(0, page - 1))}
      onNext={() => goTo(Math.min(pageCount - 1, page + 1))}
      header={
        <StageHeader name={name} page={page} total={pageCount}>
          {/* The AI deck builder — hidden with every other AI surface while
              env.featureCrAiEnabled is off. */}
          {env.featureCrAiEnabled && (
            <button
              type="button"
              aria-label={t('panel.saved.build')}
              onClick={onBuild}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-brand/40 bg-brand/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand active:opacity-80"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {!isPro && <span className="text-fg-muted">PRO</span>}
            </button>
          )}
        </StageHeader>
      }
      grid={grid}
      stats={stats}
      actions={actions}
      overlay={
        <>
          <CoachOverlay
            open={coachOpen}
            onClose={() => setCoachOpen(false)}
            isPro={isPro}
            loading={coach.isLoading}
            error={coach.error}
            analysis={coach.data?.analysis}
            usage={coach.data?.usage}
          />
          <AdvisorOverlay
            open={advisor === 'doctor'}
            title={t('panel.doctor.title')}
            onClose={() => setAdvisor(null)}
            loading={doctorState.isLoading}
            error={doctorState.error}
            onRetry={openDoctor}
          >
            {doctorState.data?.deck && (
              <DoctorResult
                deck={doctorState.data.deck}
                onSave={onDoctorSave}
                saving={saving}
                saved={doctorSaved}
                saveError={doctorSaveError}
                saveLabel={t('panel.action.save')}
                savedLabel={t('panel.action.saved')}
              />
            )}
          </AdvisorOverlay>
          <AdvisorOverlay
            open={advisor === 'counter'}
            title={t('panel.counter.title')}
            onClose={() => setAdvisor(null)}
            loading={counterState.isLoading}
            error={counterState.error}
            onRetry={openCounter}
          >
            {counterState.data && <CounterResult result={counterState.data} />}
          </AdvisorOverlay>
        </>
      }
    />
  );
}

function IncompleteDeck({ count }: { count: number }) {
  const { t } = useTranslation('cr');
  return (
    <div className="rounded-lg bg-bg-secondary p-3">
      <p className="text-xs font-semibold text-fg">{t('panel.mine.incomplete.title')}</p>
      <p className="mt-0.5 text-[11px] leading-snug text-fg-muted">
        {t('panel.mine.incomplete.body', { count })}
      </p>
    </div>
  );
}

function DeckSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      <div className="grid grid-cols-4 gap-1.5">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="aspect-square animate-pulse rounded-md bg-bg-secondary" />
        ))}
      </div>
      <div className="h-3.5 w-32 animate-pulse rounded bg-bg-secondary" />
    </div>
  );
}
